/**
 * Which salon is this page for?
 *
 * The portal runs on its own domain with one subdomain per tenant:
 *
 *     https://<tenant-slug>.book.example.com
 *
 * A path form is also supported — `https://book.example.com/t/<slug>` — because
 * wildcard TLS is not always available, and because local development runs on
 * `localhost:5279` where no subdomain exists at all.
 *
 * Subdomain wins when both are present. Hosts that are plainly not a tenant
 * (`www`, `book`, bare IPs, `localhost`) are ignored so the path form can take
 * over rather than resolving to a nonsense slug.
 */

const RESERVED_HOSTS = new Set(['www', 'book', 'booking', 'app', 'api', 'localhost', 'staging', 'dev']);
const SLUG_RE = /^[a-z0-9][a-z0-9_-]{1,63}$/i;

export interface TenantRoute {
  slug: string | null;
  /** Present when the URL is a deposit return with the charge id: /r/<slug>?tap_id=… (Tap names it) */
  chargeId: string | null;
  /**
   * Our own reference on a deposit return (`pos_ref`), which every gateway's
   * return URL carries — the charge is found by it when the provider dropped
   * or renamed its own id.
   */
  reference: string | null;
  /** Deep link into a specific booking reference, from an e-mail link. */
  bookingRef: string | null;
  /** True when the slug came from the path, so links must keep the prefix. */
  pathScoped: boolean;
}

function slugFromHost(host: string): string | null {
  const hostname = host.split(':')[0] ?? '';
  // An IPv4 literal is never a tenant.
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null;
  const labels = hostname.split('.');
  if (labels.length < 3) return null; // need <slug>.<domain>.<tld>
  const first = labels[0] ?? '';
  if (!first || RESERVED_HOSTS.has(first.toLowerCase())) return null;
  return SLUG_RE.test(first) ? first.toLowerCase() : null;
}

export function resolveTenantRoute(
  location: { hostname: string; pathname: string; search: string } = window.location,
): TenantRoute {
  const parts = location.pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  const params = new URLSearchParams(location.search);

  // /r/<slug> — the gateway sends the customer back here after a deposit.
  if (parts[0] === 'r' && parts[1]) {
    return {
      slug: SLUG_RE.test(parts[1]) ? parts[1].toLowerCase() : slugFromHost(location.hostname),
      chargeId: params.get('tap_id') ?? params.get('charge_id'),
      reference: params.get('pos_ref'),
      bookingRef: null,
      pathScoped: true,
    };
  }

  const hostSlug = slugFromHost(location.hostname);
  // /t/<slug>/… — the path fallback.
  if (parts[0] === 't' && parts[1] && SLUG_RE.test(parts[1])) {
    return {
      slug: parts[1].toLowerCase(),
      chargeId: params.get('tap_id'),
      reference: params.get('pos_ref'),
      bookingRef: params.get('ref'),
      pathScoped: true,
    };
  }

  return {
    slug: hostSlug,
    chargeId: params.get('tap_id'),
    reference: params.get('pos_ref'),
    bookingRef: params.get('ref'),
    pathScoped: false,
  };
}

/** Absolute URL back to this portal, used as the deposit return address. */
export function portalReturnUrl(route: TenantRoute): string {
  const { origin } = window.location;
  return route.pathScoped || !slugFromHost(window.location.hostname)
    ? `${origin}/r/${route.slug ?? ''}`
    : `${origin}/r/${route.slug ?? ''}`;
}

/** Drop `tap_id` etc. from the address bar once they have been consumed. */
export function stripQuery(): void {
  if (window.location.search) {
    history.replaceState(null, '', window.location.pathname);
  }
}
